import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import Select from "react-select";
import { IconArrowsExchange } from "@tabler/icons-react";
import { getPipelines, getColumns } from "../services/pipeline.service";
import { transferContactPipeline } from "../services/contacts.service";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 14,
    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(59,130,246,0.15)" : "none",
    "&:hover": { borderColor: "#94a3b8" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  menu: (base) => ({ ...base, zIndex: 10000 }),
};

const TransferPipelineModal = ({
  show,
  onClose,
  contact,
  onSuccess,
}) => {
  const [pipelines, setPipelines] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const currentPipelineId = contact?.pipeline?.id || contact?.pipeline || null;

  const pipelineOptions = useMemo(
    () =>
      pipelines
        .filter((p) => p.id !== currentPipelineId && p.is_active !== false)
        .map((p) => ({ value: p.id, label: p.name })),
    [pipelines, currentPipelineId],
  );

  const columnOptions = useMemo(
    () => columns.map((c) => ({ value: c.id, label: c.name })),
    [columns],
  );

  useEffect(() => {
    if (!show) return;
    setError(null);
    setSelectedPipeline(null);
    setSelectedColumn(null);
    setColumns([]);

    setLoadingPipelines(true);
    getPipelines()
      .then((data) => {
        const list = data?.results || data || [];
        setPipelines(list);
      })
      .catch((err) => {
        setError("Falha ao carregar pipelines. Tente de novo.");
      })
      .finally(() => setLoadingPipelines(false));
  }, [show]);

  useEffect(() => {
    if (!selectedPipeline) {
      setColumns([]);
      setSelectedColumn(null);
      return;
    }
    setLoadingColumns(true);
    getColumns(selectedPipeline.value)
      .then((data) => {
        const list = data?.results || data || [];
        setColumns(list);
      })
      .catch(() => {
        setError("Falha ao carregar colunas do pipeline.");
        setColumns([]);
      })
      .finally(() => setLoadingColumns(false));
  }, [selectedPipeline]);

  const handleSubmit = async () => {
    if (!selectedPipeline) {
      setError("Selecione um pipeline destino.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const updated = await transferContactPipeline(
        contact.id,
        selectedPipeline.value,
        selectedColumn?.value || null,
      );
      if (onSuccess) onSuccess(updated);
      onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Erro ao transferir contato.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
          <IconArrowsExchange size={20} />
          Transferir contato de pipeline
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {contact && (
          <div style={{ marginBottom: 16, fontSize: 14, color: "#475569" }}>
            <div><strong>Contato:</strong> {contact.name || "—"}</div>
            <div><strong>Pipeline atual:</strong> {contact.pipeline_name || contact.pipeline?.name || "—"}</div>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" }}>
            Pipeline destino <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <Select
            options={pipelineOptions}
            value={selectedPipeline}
            onChange={setSelectedPipeline}
            placeholder={loadingPipelines ? "Carregando..." : "Selecione um pipeline"}
            isLoading={loadingPipelines}
            isDisabled={loadingPipelines || submitting}
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, display: "block" }}>
            Coluna destino (opcional)
          </label>
          <Select
            options={columnOptions}
            value={selectedColumn}
            onChange={setSelectedColumn}
            placeholder={
              !selectedPipeline
                ? "Selecione um pipeline primeiro"
                : loadingColumns
                  ? "Carregando colunas..."
                  : "Padrão: primeira coluna do pipeline"
            }
            isLoading={loadingColumns}
            isDisabled={!selectedPipeline || loadingColumns || submitting}
            isClearable
            styles={selectStyles}
            menuPortalTarget={document.body}
          />
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
            Se vazio, o contato cai na primeira coluna do pipeline destino.
          </div>
        </div>

        {error && (
          <Alert variant="danger" style={{ fontSize: 13, padding: "8px 12px", marginBottom: 0 }}>
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!selectedPipeline || submitting}
        >
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" /> Transferindo...
            </>
          ) : (
            "Transferir"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferPipelineModal;
